import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { PreviewFrame, type Viewport } from '@/components/builder/PreviewFrame';
import { VersionsPanel } from '@/components/builder/VersionsPanel';
import { SiteSettingsPanel } from '@/components/builder/SiteSettingsPanel';
import { LeadsPanel } from '@/components/builder/LeadsPanel';
import { EditorToolbar } from '@/components/builder/EditorToolbar';
import { ChatPanel } from '@/components/builder/ChatPanel';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import {
  builderApi,
  type Build,
  type BuilderMessage,
  type BuildLead,
  type BuildVersionMeta,
} from '@/utils/api-pb';

const GENERATION_ESTIMATE_SECONDS = 45;

type Panel = 'versions' | 'settings' | 'leads' | null;

export default function BuilderEditorPage() {
  const { t, i18n } = useTranslation('builder');
  const { buildId = '' } = useParams();
  const navigate = useLocalizedNavigate();

  const [build, setBuild] = useState<Build | null>(null);
  const [html, setHtml] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [versions, setVersions] = useState<BuildVersionMeta[]>([]);
  const [messages, setMessages] = useState<BuilderMessage[]>([]);
  const [leads, setLeads] = useState<BuildLead[]>([]);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [panel, setPanel] = useState<Panel>(null);
  const [publishingEnabled, setPublishingEnabled] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);

  /** Pulls the current version's HTML. Called after every change. */
  const refreshPreview = useCallback(async () => {
    const preview = await builderApi.preview(buildId);
    setHtml(preview.html);
    setVersion(preview.version);
  }, [buildId]);

  const refreshVersions = useCallback(async () => {
    setVersions(await builderApi.listVersions(buildId));
  }, [buildId]);

  const refreshMessages = useCallback(async () => {
    setMessages(await builderApi.listMessages(buildId));
  }, [buildId]);

  /**
   * A change described in words.
   *
   * The reply is always shown, including when nothing changed — the model declining to
   * invent testimonials is an answer the owner needs to read, not a silent no-op.
   */
  const handleChat = useCallback(
    async (message: string) => {
      setIsChatting(true);
      try {
        const result = await builderApi.chat(buildId, message, i18n.language as 'ru' | 'en');
        await refreshMessages();

        if (result.changed) {
          await Promise.all([refreshPreview(), refreshVersions()]);
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('toast.error'));
        await refreshMessages().catch(() => {});
      } finally {
        setIsChatting(false);
      }
    },
    [buildId, i18n.language, refreshMessages, refreshPreview, refreshVersions, t],
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const data = await builderApi.get(buildId);
        if (cancelled) return;
        setBuild(data.build);
        setPublishingEnabled(data.publishingEnabled);

        if (data.currentVersion) {
          await Promise.all([refreshPreview(), refreshVersions(), refreshMessages()]);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : t('toast.error'));
          navigate('/builder');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [buildId, refreshPreview, refreshVersions, refreshMessages, navigate, t]);

  /**
   * A text edit made directly in the preview.
   *
   * Goes through the same operation pipeline as an AI edit, so it lands in the same
   * version history and can be rolled back the same way.
   */
  const handleEditText = useCallback(
    async (sectionId: string, field: string, value: string) => {
      setIsBusy(true);
      try {
        await builderApi.applyOps(buildId, [{ op: 'set_text', sectionId, field, value }]);
        await Promise.all([refreshPreview(), refreshVersions()]);
        toast.success(t('toast.edited'));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('toast.error'));
        // The frame still shows the user's typing; re-render to put it back in sync.
        await refreshPreview().catch(() => {});
      } finally {
        setIsBusy(false);
      }
    },
    [buildId, refreshPreview, refreshVersions, t],
  );

  async function handleRestore(target: number) {
    setIsBusy(true);
    try {
      await builderApi.restore(buildId, target);
      await Promise.all([refreshPreview(), refreshVersions()]);
      toast.success(t('toast.restored', { n: target }));
      setPanel(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleGenerate() {
    if (!build) return;
    setIsGenerating(true);
    try {
      const result = await builderApi.generate(
        buildId,
        build.brief as Record<string, unknown>,
        i18n.language as 'ru' | 'en',
      );
      await Promise.all([refreshPreview(), refreshVersions()]);
      toast[result.degraded ? 'warning' : 'success'](
        result.degraded ? t('toast.generatedDegraded') : t('toast.generated'),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSaveSettings(patch: { name?: string; slug?: string }) {
    setIsBusy(true);
    try {
      setBuild(await builderApi.update(buildId, patch));
      toast.success(t('toast.saved'));
      setPanel(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  /**
   * Publishing, and republishing, are the same call.
   *
   * The build record is reloaded afterwards rather than patched locally: the address
   * and the status shown in the toolbar come from the server, and guessing them here
   * is how a UI ends up claiming a site is live when it is not.
   */
  async function handlePublish() {
    setIsBusy(true);
    try {
      const result = await builderApi.publish(buildId);
      const data = await builderApi.get(buildId);
      setBuild(data.build);
      toast.success(t('toast.published', { url: result.url.replace(/^https:\/\//, '') }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleUnpublish() {
    setIsBusy(true);
    try {
      await builderApi.unpublish(buildId);
      const data = await builderApi.get(buildId);
      setBuild(data.build);
      toast.success(t('toast.unpublished'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  /** Leads are fetched only when the panel opens — most sessions never look at them. */
  async function openPanel(next: Exclude<Panel, null>) {
    setPanel(next);
    if (next !== 'leads') return;

    setIsLoadingLeads(true);
    try {
      setLeads(await builderApi.listLeads(buildId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsLoadingLeads(false);
    }
  }

  async function handleExportLeads() {
    try {
      await builderApi.downloadLeadsCsv(buildId, `leads-${build?.slug || buildId}.csv`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    }
  }

  async function handleDeleteLead(leadId: string) {
    setIsBusy(true);
    try {
      await builderApi.deleteLead(buildId, leadId);
      setLeads((current) => current.filter((lead) => lead.id !== leadId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsBusy(false);
    }
  }

  if (isLoading) {
    return <AccessibleLoader label={t('loading', { ns: 'common' })} />;
  }

  if (!build) return null;

  if (isGenerating) {
    return (
      <GenerationProgress
        title={t('generating.title')}
        message={t('generating.message')}
        estimatedDuration={GENERATION_ESTIMATE_SECONDS}
      />
    );
  }

  return (
    // Fills the viewport so the preview frame can scroll inside its own bounds.
    <div className="flex h-full min-h-0 flex-col gap-[12px]">
      <EditorToolbar
        build={build}
        version={version}
        viewport={viewport}
        publishingEnabled={publishingEnabled}
        isBusy={isBusy || isChatting}
        hasContent={Boolean(html)}
        onViewport={setViewport}
        onBack={() => navigate('/builder')}
        onOpenPanel={openPanel}
        onRegenerate={handleGenerate}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
      />

      {html ? (
        <div className="flex min-h-0 flex-1 flex-col gap-[12px] lg:flex-row">
          <div className="flex min-h-0 flex-1 flex-col gap-[8px]">
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#969696)]">
              {t('editor.editHint')}
            </p>
            <PreviewFrame
              html={html}
              viewport={viewport}
              busy={isBusy || isChatting}
              onEditText={handleEditText}
            />
          </div>

          <ChatPanel
            messages={messages}
            isSending={isChatting}
            disabled={isBusy}
            onSend={handleChat}
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-[12px] text-center">
          <h2 className="font-manrope text-[20px] font-bold text-[color:var(--black-text,#0d0d0d)]">
            {t('editor.empty.title')}
          </h2>
          <p className="font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
            {t('editor.empty.text')}
          </p>
          <Button variant="brand" size="brand" onClick={handleGenerate}>
            {t('editor.empty.action')}
          </Button>
        </div>
      )}

      <Dialog open={panel !== null} onOpenChange={(open) => !open && setPanel(null)}>
        <DialogContent className="max-h-[90vh] max-w-[520px] overflow-y-auto p-6 sm:p-8">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center font-manrope text-[24px] font-bold">
              {t(`${panel ?? 'versions'}.title`)}
            </DialogTitle>
          </DialogHeader>

          {panel === 'settings' && (
            <SiteSettingsPanel build={build} isSaving={isBusy} onSave={handleSaveSettings} />
          )}

          {panel === 'leads' && (
            <LeadsPanel
              leads={leads}
              isLoading={isLoadingLeads}
              isBusy={isBusy}
              onExport={handleExportLeads}
              onDelete={handleDeleteLead}
            />
          )}

          {panel === 'versions' && (
            <VersionsPanel
              versions={versions}
              currentVersion={version}
              isBusy={isBusy}
              onRestore={handleRestore}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
